import { Divider, Stack } from '@mui/material';
import { SexUpper } from 'primary-schema';
import { z } from 'zod';

import { api } from 'common';

import { OptionalBaseEncounterParamsFormFields } from './Common/Encounters';
import { OrderJobParamsFormFields, OrderJobParamsSchema } from './Common/Orders';
import { OrderProcedures } from './OrderProcedures';
import {
  type AsynchronousJobVariant,
  OptionalStringSchema,
  PatientSexUpperSchema,
  RequiredStringSchema,
  getEnumOrFlowTemplateVariableSchema,
} from './common';
import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockSelectField } from '../../../ConfigFields/SelectField';

import JobKind = api.RpaJob.Kind;

const timeZone = [
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
];

export const ExportReferralOrderVariant: AsynchronousJobVariant = {
  schema: z.object({
    jobKind: z.literal(JobKind.ExportReferralOrder),
    payload: OrderJobParamsSchema.merge(
      z.object({
        patientFirstName: RequiredStringSchema,
        patientLastName: RequiredStringSchema,
        patientDateOfBirth: RequiredStringSchema,
        patientSex: PatientSexUpperSchema,
        referredByProviderFirstName: RequiredStringSchema,
        referredByProviderLastName: RequiredStringSchema,
        referredByProviderNpi: RequiredStringSchema,
        rawDiagnoses: RequiredStringSchema,
        referralComment: OptionalStringSchema,
        referralPriority: getEnumOrFlowTemplateVariableSchema([
          'ROUTINE',
          'STAT',
          'URGENT',
        ]).optional(),
        orderSpecificQuestions: OptionalStringSchema,
        procedures: z.union([
          z
            .array(
              z.object({
                description: RequiredStringSchema,
                cptCode: OptionalStringSchema,
                areaOfInterest: OptionalStringSchema,
              })
            )
            .optional(),
          OptionalStringSchema,
        ]),
        department: OptionalStringSchema,
        expectedProcedureDate: OptionalStringSchema,
        orderClass: OptionalStringSchema,
        timezone: getEnumOrFlowTemplateVariableSchema(timeZone).optional(),
      })
    ),
  }),
  Config: () => {
    return (
      <Stack spacing={2}>
        <BlockValueTemplateField
          fieldName="config.payload.patientFirstName"
          label="Patient First Name"
          helperText="Enter the reference to the patient's first name. e.g. `{{patient.data.firstName}}`"
          placeholder="e.g. {{patient.data.firstName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientLastName"
          label="Patient Last Name"
          helperText="Enter the reference to the patient's last name. e.g. `{{patient.data.lastName}}`"
          placeholder="e.g. {{patient.data.lastName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientDateOfBirth"
          label="Patient Date of Birth"
          helperText="Enter the reference to the patient's date of birth. e.g. `{{patient.data.dateOfBirth}}`"
          placeholder="e.g. {{patient.data.dateOfBirth}}"
        />
        <BlockSelectField
          fieldName="config.payload.patientSex"
          label="Patient Sex"
          helperText="Select the patient's sex, or use a flow template (e.g. `{{patient.data.sex}}`) to reference a dynamic value."
          fieldVariant="SelectOrBlockValueTemplate"
          options={sexOptions}
        />
        <Divider />
        <BlockValueTemplateField
          fieldName="config.payload.referredByProviderFirstName"
          label="Referred By Provider First Name"
          helperText="Enter the reference to the Referred By Provider First Name. e.g. `{{patient.data.referredByProviderFirstName}}`"
          placeholder="e.g. {{order.data.referredByProviderFirstName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referredByProviderLastName"
          label="Referred By Provider Last Name"
          helperText="Enter the reference to the Referred By Provider Last Name. e.g. `{{patient.data.referredByProviderLastName}}`"
          placeholder="e.g. {{order.data.referredByProviderLastName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referredByProviderNpi"
          label="Referred By Provider Npi"
          helperText="Enter the reference to the Referred By Provider Npi. e.g. `{{patient.data.referredByProviderNpi}}`"
          placeholder="e.g. {{order.data.referredByProviderNpi}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.rawDiagnoses"
          label="Raw Diagnoses"
          helperText="Enter the reference to the Raw Diagnoses. e.g. `{{patient.data.rawDiagnoses}}`. Should be a comma separated list of ICD10 Codes"
          placeholder="e.g. {{order.data.rawDiagnoses}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referralComment"
          label="Referral Comment"
          helperText="Enter the reference to the Referral Comment. e.g. `{{patient.data.referralComment}}`"
          placeholder="e.g. {{order.data.referralComment}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referralPriority"
          label="Referral Priority"
          helperText="Enter the reference to the Referral Priority. e.g. `{{patient.data.referralPriority}}`"
          placeholder="e.g. {{order.data.referralPriority}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.orderSpecificQuestions"
          label="Order Specific Questions"
          helperText="Enter the reference to the Order Specific Questions. e.g. `{{patient.data.orderSpecificQuestions}}`"
          placeholder="e.g. {{order.data.orderSpecificQuestions}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.department"
          label="Department"
          helperText="Enter the reference to the Department. e.g. `{{patient.data.department}}`"
          placeholder="e.g. {{order.data.department}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.expectedProcedureDate"
          label="Expected Procedure Date"
          helperText="Enter the reference to the Expected Procedure Date. e.g. `{{patient.data.expectedProcedureDate}}`"
          placeholder="e.g. {{order.data.expectedProcedureDate}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.orderClass"
          label="Order Class"
          helperText="Enter the reference to the Order Class. e.g. `{{patient.data.orderClass}}`"
          placeholder="e.g. {{order.data.orderClass}}"
        />
        <BlockSelectField
          fieldName="config.payload.timezone"
          label="Time Zone"
          helperText="Select the Time Zone in which the current date time will be exported."
          options={timeZone.map((value) => ({
            label: value,
            value,
          }))}
        />
        <Divider />
        <OrderProcedures />
        <Divider />
        <OrderJobParamsFormFields />
        <Divider />
        <OptionalBaseEncounterParamsFormFields />
      </Stack>
    );
  },
};

const sexOptions = Object.values(SexUpper).map((sex) => ({
  label: sex,
  value: sex,
}));
